import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuditLogs, type AuditLogEntry } from '@/api/hooks/useSettings';
import { RequireRole } from '@/components/auth/RequireRole';
import { cn } from '@/lib/utils';

const SELECT_CLS = 'rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-1.5 text-sm text-text-primary focus:outline-none';

const PERIOD_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d',  label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

const CATEGORY_OPTIONS = [
  { value: '',                   label: 'All categories' },
  { value: 'authentication',     label: 'Authentication' },
  { value: 'user_mgmt',          label: 'User Management' },
  { value: 'data_access',        label: 'Data Access' },
  { value: 'data_modification',  label: 'Data Modification' },
  { value: 'config_change',      label: 'Config Change' },
  { value: 'security_event',     label: 'Security Event' },
  { value: 'ai_action',          label: 'AI Action' },
  { value: 'compliance_event',   label: 'Compliance Event' },
  { value: 'billing',            label: 'Billing' },
];

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'text-red-400',
  high:     'text-orange-400',
  medium:   'text-yellow-400',
  low:      'text-blue-400',
  info:     'text-text-secondary',
};

const OUTCOME_COLORS: Record<string, string> = {
  success: 'text-emerald-400',
  failure: 'text-red-400',
  denied:  'text-orange-400',
};

function userName(entry: AuditLogEntry): string {
  return entry.user_display_name || entry.user_email || 'System';
}

function exportCsv(rows: AuditLogEntry[]) {
  const headers = ['Date/Time', 'User', 'Email', 'Category', 'Action', 'Outcome', 'Resource Type', 'Resource', 'Severity', 'IP Address'];
  const escape = (v: string | null | undefined) => `"${(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    headers.join(','),
    ...rows.map(r => [
      escape(r.created_at),
      escape(r.user_display_name),
      escape(r.user_email),
      escape(r.event_category),
      escape(r.action),
      escape(r.outcome),
      escape(r.resource_type),
      escape(r.resource_name),
      escape(r.event_severity),
      escape(r.ip_address),
    ].join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function AuditLogPage() {
  return (
    <RequireRole roles={['risk_manager', 'super_admin']} label="Activity Log">
      <AuditLogPageBody />
    </RequireRole>
  );
}

function AuditLogPageBody() {
  useEffect(() => { document.title = 'Activity Log — VeriGRC'; }, []);
  const [period,   setPeriod]   = useState('7d');
  const [category, setCategory] = useState('');
  const [page,     setPage]     = useState(1);

  const { data, isLoading, isError } = useAuditLogs({ period, category: category || undefined, page });
  const rows  = data?.data  ?? [];
  const meta  = data?.meta;

  function handlePeriodChange(val: string) { setPeriod(val); setPage(1); }
  function handleCategoryChange(val: string) { setCategory(val); setPage(1); }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Activity Log</h1>
        <p className="text-sm text-text-secondary mt-1">
          Review all activity in your workspace. Log entries are retained for 90 days.
        </p>
      </div>

      {/* Filters + Export */}
      <div className="flex flex-wrap items-center gap-3">
        <select value={period} onChange={(e) => handlePeriodChange(e.target.value)} className={SELECT_CLS}>
          {PERIOD_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select value={category} onChange={(e) => handleCategoryChange(e.target.value)} className={SELECT_CLS}>
          {CATEGORY_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => rows.length > 0 && exportCsv(rows)}
            disabled={rows.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border-subtle overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-surface border-b border-border-subtle">
              <tr>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Date / Time</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">User</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Action</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Category</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Resource</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Outcome</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Severity</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-text-secondary">
                    Loading…
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-red-400">
                    Failed to load audit log. Please try again.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-text-secondary">
                    No audit log entries found for the selected filters.
                  </td>
                </tr>
              )}
              {rows.map(row => (
                <tr key={row.id} className="border-b border-border-subtle last:border-0 hover:bg-bg-surface/50">
                  <td className="px-4 py-3 whitespace-nowrap text-text-secondary font-mono text-xs">
                    {format(new Date(row.created_at), 'dd MMM yyyy HH:mm:ss')}
                  </td>
                  <td className="px-4 py-3 max-w-[180px]">
                    <div className="truncate text-text-primary">{userName(row)}</div>
                    {row.user_email && row.user_display_name && (
                      <div className="truncate text-xs text-text-secondary">{row.user_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-text-primary max-w-[200px]">
                    <div className="truncate">{row.action}</div>
                    <div className="text-xs text-text-secondary truncate">{row.event_type}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-bg-surface border border-border-subtle text-text-secondary capitalize">
                      {row.event_category.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-text-secondary max-w-[160px]">
                    {row.resource_name ? (
                      <>
                        <div className="truncate text-text-primary">{row.resource_name}</div>
                        {row.resource_type && (
                          <div className="truncate text-xs text-text-secondary">{row.resource_type}</div>
                        )}
                      </>
                    ) : (
                      <span className="text-text-secondary/40">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('capitalize font-medium', OUTCOME_COLORS[row.outcome] ?? 'text-text-secondary')}>
                      {row.outcome}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('capitalize text-xs', SEVERITY_COLORS[row.event_severity] ?? 'text-text-secondary')}>
                      {row.event_severity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {meta && meta.pages > 1 && (
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <span>
            {((meta.page - 1) * meta.limit) + 1}–{Math.min(meta.page * meta.limit, meta.total)} of {meta.total} entries
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>Page {meta.page} of {meta.pages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(meta.pages, p + 1))}
              disabled={page >= meta.pages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
